import { DisabledPrompt, useBCMSContext, useStateManager, BCMSRecord, StateManager } from "bcms-react";
import Classes from "model";
import { TableBody, TableColumnContainer, TableColumnView, TableView, WidgetContainer, WidgetView } from 'components/shared';
import { PrimaryButton, UICheckbox, UITextInput } from 'components/ui';
import React from 'react';
import { SVGAsset } from 'res';
import NoWireTransferTableRow from '../NoWireTransferTableRow';
import PendingWireWidgetTableRow from "../PendingWireWidgetTableRow";

import "./mobile.css";
import "./styles.css";

/**
 * @function PendingWireWidget
 * @return {React.Component}
 * @exported true
 * @description Shows the pending wire widgets
 */

export default function PendingWireWidget(props) {
  const globalController = useBCMSContext();
  BCMSRecord.globalControllerFor(Classes, globalController);

  const data = { ...getDefaultData(), ...props.data ?? {} };
  const ui = { ...getDefaultUI(), ...props.ui ?? {} };

  const controller = (state, setState) => ({
      data: state.data,
      ui: state.ui,
  });

  return(
    <StateManager state={ { data, ui } } controller={ controller }>
      <WidgetContent/>
    </StateManager>
  )
}

export const getDefaultData = () => ({
  // no data used for now
});

export const getDefaultUI = () => ({
  // no ui changes for now
});

function WidgetContent(props) {
  const { data, ui } = useStateManager();

  const desktopRows = data.oneTimeWireTransfers.reduce((acc, item) => {
    acc.push(
      <PendingWireWidgetTableRow key={ item.id } oneTimeWireTransfer={ item } />
    );
    return acc;
  }, []);

  const mobileRows = data.oneTimeWireTransfers.reduce((acc, item) => {
    acc.push(
      <MobilePendingWireRow key={ item.id } oneTimeWireTransfer={ item } />
    );
    return acc;
  }, []);
  
  return (
    <WidgetContainer className="pending-wire-widget">
      <WidgetView text="Pending Wires">
        
        {/* Desktop Pending wires table view */}
        <div className="show-on-desktop">
          <TableView className="pending-wires-table">
            <TableColumnContainer>
              <TableColumnView className="text-left ui-checkbox-column" text=
                {
                  <DisabledPrompt>
                    <UICheckbox label="" />
                  </DisabledPrompt>
                }
              />
              <TableColumnView className="text-left payee-text" text=
                {<>Payee
                  <DisabledPrompt>
                    <SVGAsset id="ArrowUpIcon" className="clickable" />
                  </DisabledPrompt>
                </>}
              />
              <TableColumnView className="text-left text-right amount-text" text="Amount" />
              <TableColumnView className="text-left status-text" text="Status" />
              <TableColumnView className="text-left wire-date" text="Wire Date" />
              <TableColumnView className="text-left initiated-by-text" text="Initiated By" />
              <TableColumnView className="text-left comment-text" text="Comment" />
              <TableColumnView className="text-left actions-column" />
            </TableColumnContainer>
            <TableBody>
              { desktopRows.length > 0 && desktopRows }
              { desktopRows.length === 0 && <NoWireTransferTableRow colspan="7" /> }
            </TableBody>
          </TableView>
        </div>
        
        {/* Mobile Pending wires table view */}
        <div className="mobile-pending-wire-table show-on-mobile">
        { mobileRows.length > 0 && mobileRows }
        </div>
        
        <div className="show-on-mobile">
          <TableView className="pending-wires-table">
            <TableBody>
            { mobileRows.length === 0 && <NoWireTransferTableRow colspan="7" /> }
            </TableBody>
          </TableView>
        </div>

        <div className="update-comment-container show-on-mobile">
          <UITextInput placeholder="Update comment"/>
        </div>


        <div className="button-container flex-container flex-horizontal">
          <PrimaryButton className="disabled-button reject-button" dataPoint="Pending_Wire_Widget_Reject_Button" text="Reject" />
          <PrimaryButton className="disabled-button approve-button" dataPoint="Pending_Wire_Widget_Approve_Button" text="Approve" />
        </div>
        
      </WidgetView>
    </WidgetContainer>
  );
}

/**
 * @function MobilePendingWireRow
 * @return {React.Component}
 * @exported false
 * @description The row that shows in the MobilePendingWireTable
 */
function MobilePendingWireRow(props) {
  const oneTimeWireTransfer = props.oneTimeWireTransfer ?? {};
  const fundingAccount = `${ oneTimeWireTransfer.fundingAccount.number } - ${ oneTimeWireTransfer.fundingAccount.name }`;
  const referenceNumber = props.referenceNumber ?? "[referenceNumber]";
  const currency = props.currency ?? "USD";
  const amount = props.amount ?? "[amount]";
  const wireDate = props.wireDate ?? "[wireDate]";
  const pendingApprovals = props.pendingApprovals ?? "[0]";
  
  return(
    <div className="mobile-pending-wire-row flex-container flex-horizontal align-center space-between">
      <div className="wire-details-column flex-width">
        <div className="flex-container flex-horizontal space-between wires-item">
          <b className="flex-item set-width">{ oneTimeWireTransfer.referenceNumber }</b>
          <p className="set-item flex-width text-right">{ oneTimeWireTransfer.amount.format() }</p>
        </div>
        <div className="flex-container flex-horizontal space-between wires-item">
          <p className="flex-item set-width">Currency:</p>
          <p className="set-item flex-width text-right">{ oneTimeWireTransfer.currency }</p>
        </div>
        <div className="flex-container flex-horizontal space-between wires-item">
          <p className="flex-item set-width">Amount in $:</p>
          <p className="set-item flex-width text-right">{ oneTimeWireTransfer.amount.format() }</p>
        </div>
        <div className="flex-container flex-horizontal space-between wires-item">
          <p className="flex-item set-width">Wire Date:</p>
          <p className="set-item flex-width text-right">{ oneTimeWireTransfer.date.format("m/d/Y") }</p>
        </div>
        <div className="flex-container flex-horizontal space-between wires-item status">
          <p className="flex-item set-width">Status:</p>
          <p className="set-item flex-width text-right">
            <span className="pending-approval-text">Pending Approval(s):</span> <span className="status-text">{ oneTimeWireTransfer.numberOfPendingApprovals }</span>
          </p>
        </div>
      </div>
      <DisabledPrompt><SVGAsset id="MagnifyingGlassIcon" className="inline-text-icon left-icon clickable set-width" /></DisabledPrompt>
      <DisabledPrompt><SVGAsset id="TrashcanXIcon" className="inline-text-icon right-icon clickable set-width" /></DisabledPrompt>
    </div>
  );
}